import { useState } from 'react';
import { AreaChart, CompositeChart } from '@mantine/charts';
import { ColorSwatch, Group, SegmentedControl, Stack, Text, UnstyledButton } from '@mantine/core';
import type { MonthRevenue } from 'src/secret-menu/billing/hooks/useRevenue';
import SectionCard from '../overview/SectionCard';
import { monthKeyOfDate, monthLabelOf } from '../overview/usageModel';
import { formatUnits, formatRevenue } from './ui';

type Measure = 'revenue' | 'units';

type RevenueSeries = 'perUnit' | 'fixed' | 'total';

const REVENUE_SERIES: {
  name: RevenueSeries;
  label: string;
  color: string;
  type: 'area' | 'line';
  strokeDasharray?: string;
}[] = [
  { name: 'perUnit', label: 'Per family', color: 'blue.6', type: 'area' },
  { name: 'fixed', label: 'Fixed fee', color: 'violet.6', type: 'area' },
  { name: 'total', label: 'Total', color: 'dark.6', type: 'line', strokeDasharray: '5 5' },
];

/** The chart's legend, where clicking a name hides or shows its line. */
function ToggleLegend({
  hidden,
  onToggle,
}: {
  hidden: ReadonlySet<RevenueSeries>;
  onToggle: (name: RevenueSeries) => void;
}) {
  return (
    <Group gap="md" justify="flex-end">
      {REVENUE_SERIES.map(series => (
        <UnstyledButton
          key={series.name}
          onClick={() => onToggle(series.name)}
          aria-pressed={!hidden.has(series.name)}
          style={{ opacity: hidden.has(series.name) ? 0.4 : 1 }}
        >
          <Group gap={6} wrap="nowrap">
            <ColorSwatch
              color={`var(--mantine-color-${series.color.replace('.', '-')})`}
              size={12}
            />
            <Text size="textSm">{series.label}</Text>
          </Group>
        </UnstyledButton>
      ))}
    </Group>
  );
}

const MEASURES: { value: Measure; label: string }[] = [
  { value: 'revenue', label: 'Monthly revenue' },
  { value: 'units', label: 'Billed units' },
];

/**
 * Adviser revenue at each month-end, from the nightly billing snapshots only. The current month's
 * snapshot is overwritten every night until the month closes, so it is marked as in progress.
 */
export default function RevenueHistoryChart({
  history,
  today,
}: {
  history: readonly MonthRevenue[];
  today: Date;
}) {
  const [measure, setMeasure] = useState<Measure>('revenue');
  const [hidden, setHidden] = useState<ReadonlySet<RevenueSeries>>(new Set());
  const toggle = (name: RevenueSeries) =>
    setHidden(current => {
      const next = new Set(current);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  const thisMonth = monthKeyOfDate(today);
  const labelOf = (month: string) => {
    const key = month.slice(0, 7);
    return key === thisMonth ? `${monthLabelOf(key)} (so far)` : monthLabelOf(key);
  };

  let body: React.ReactNode;
  if (history.length === 0) {
    body = (
      <Text size="textSm" c="dimmed">
        No billing snapshots yet. Billing is captured every night, and the chart starts once two
        months have been captured.
      </Text>
    );
  } else if (history.length === 1) {
    const [only] = history;
    body = (
      <Text size="textSm" c="dimmed">
        Only {labelOf(only.month)} has been captured: {formatRevenue(only.adviserMrrPence)} a month
        from {formatUnits(only.billedUnits)} billed units. The chart starts once there are two
        months.
      </Text>
    );
  } else if (measure === 'revenue') {
    body = (
      <Stack gap="xs">
        <ToggleLegend hidden={hidden} onToggle={toggle} />
        <CompositeChart
          h={240}
          data={history.map(entry => ({
            month: labelOf(entry.month),
            perUnit: entry.perUnitMrrPence,
            fixed: entry.fixedMrrPence,
            total: entry.adviserMrrPence,
          }))}
          dataKey="month"
          series={REVENUE_SERIES.filter(series => !hidden.has(series.name))}
          areaProps={{ stackId: 'revenue' }}
          valueFormatter={formatRevenue}
          curveType="linear"
        />
      </Stack>
    );
  } else {
    body = (
      <AreaChart
        h={240}
        data={history.map(entry => ({ month: labelOf(entry.month), units: entry.billedUnits }))}
        dataKey="month"
        series={[{ name: 'units', label: 'Billed units', color: 'green.6' }]}
        valueFormatter={formatUnits}
        curveType="linear"
      />
    );
  }

  return (
    <SectionCard
      title="Adviser firm revenue"
      subtitle="Adviser firms' monthly bills at each month-end, from the nightly billing snapshot. The strategic partner is entered by hand, so it has no history."
      action={
        history.length >= 2 && (
          <SegmentedControl
            size="xs"
            data={MEASURES}
            value={measure}
            onChange={value => setMeasure(value as Measure)}
          />
        )
      }
    >
      {body}
    </SectionCard>
  );
}
